import React, { useState } from 'react';
import {
  Megaphone, Image, FileText, Video, Monitor, Download, ExternalLink,
  Search, Filter
} from 'lucide-react';

// ============================================================
// GOOGLE DRIVE INTEGRATION
// Replace each googleDriveLink with your actual Google Drive/
// Docs shared links. Format: https://drive.google.com/file/d/FILE_ID/view
// or for folders: https://drive.google.com/drive/folders/FOLDER_ID
// Make sure files are set to "Anyone with the link can view"
// ============================================================

const ASSETS = [
  {
    id: 1,
    name: 'Neurogenx Patient Brochure',
    desc: 'Tri-fold brochure explaining the treatment. Print-ready PDF.',
    type: 'Print',
    category: 'Brochures',
    format: 'PDF',
    updated: 'Jan 2026',
    icon: FileText,
    iconClass: 'pdf',
    previewClass: '',
    googleDriveLink: 'https://drive.google.com/file/d/YOUR_FILE_ID/view',
  },
  {
    id: 2,
    name: 'Social Media Pack — Instagram',
    desc: '12 branded post templates for Instagram. Canva format included.',
    type: 'Digital',
    category: 'Social Media',
    format: 'CANVA / PNG',
    updated: 'Feb 2026',
    icon: Image,
    iconClass: 'form',
    previewClass: 'teal',
    googleDriveLink: 'https://drive.google.com/drive/folders/YOUR_FOLDER_ID',
  },
  {
    id: 3,
    name: 'Facebook Ad Templates',
    desc: '6 ad designs (1200×628). Editable Canva templates.',
    type: 'Digital',
    category: 'Social Media',
    format: 'CANVA',
    updated: 'Feb 2026',
    icon: Monitor,
    iconClass: 'form',
    previewClass: 'teal',
    googleDriveLink: 'https://drive.google.com/drive/folders/YOUR_FOLDER_ID',
  },
  {
    id: 4,
    name: 'Patient Success Video',
    desc: 'Testimonial-style video for waiting room display. MP4 & WebM.',
    type: 'Video',
    category: 'Video',
    format: 'MP4',
    updated: 'Nov 2025',
    icon: Video,
    iconClass: 'video',
    previewClass: 'pink',
    googleDriveLink: 'https://drive.google.com/file/d/YOUR_FILE_ID/view',
  },
  {
    id: 5,
    name: 'Waiting Room Poster — 18"×24"',
    desc: 'High-resolution poster for clinic waiting areas. Print-ready.',
    type: 'Print',
    category: 'Posters',
    format: 'PDF',
    updated: 'Dec 2025',
    icon: Image,
    iconClass: 'pdf',
    previewClass: '',
    googleDriveLink: 'https://drive.google.com/file/d/YOUR_FILE_ID/view',
  },
  {
    id: 6,
    name: 'Email Newsletter Template',
    desc: 'Patient re-engagement email. HTML template, editable in Mailchimp.',
    type: 'Digital',
    category: 'Email',
    format: 'HTML',
    updated: 'Jan 2026',
    icon: FileText,
    iconClass: 'doc',
    previewClass: '',
    googleDriveLink: 'https://drive.google.com/file/d/YOUR_FILE_ID/view',
  },
  {
    id: 7,
    name: 'Neurogenx Logo Pack',
    desc: 'Full brand logo package. PNG, SVG, EPS in all color variants.',
    type: 'Brand',
    category: 'Brand Assets',
    format: 'ZIP',
    updated: 'Sep 2025',
    icon: Image,
    iconClass: 'doc',
    previewClass: '',
    googleDriveLink: 'https://drive.google.com/drive/folders/YOUR_FOLDER_ID',
  },
  {
    id: 8,
    name: 'Google Review Request Card',
    desc: 'Print card with QR code prompting patient reviews. Customizable.',
    type: 'Print',
    category: 'Print',
    format: 'PDF',
    updated: 'Jan 2026',
    icon: FileText,
    iconClass: 'pdf',
    previewClass: 'pink',
    googleDriveLink: 'https://drive.google.com/file/d/YOUR_FILE_ID/view',
  },
];

const CATEGORIES = ['All', 'Brochures', 'Social Media', 'Video', 'Posters', 'Email', 'Brand Assets', 'Print'];

export default function MarketingPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  const filtered = ASSETS.filter(a => {
    const matchCat = activeCategory === 'All' || a.category === activeCategory;
    const matchSearch = a.name.toLowerCase().includes(search.toLowerCase()) ||
      a.desc.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const handleDownload = (asset) => {
    // Open Google Drive link in new tab
    if (asset.googleDriveLink && !asset.googleDriveLink.includes('YOUR_FILE_ID') && !asset.googleDriveLink.includes('YOUR_FOLDER_ID')) {
      window.open(asset.googleDriveLink, '_blank');
    } else {
      alert('This asset link has not been configured yet.\n\nReplace the googleDriveLink in MarketingPage.jsx with your actual Google Drive shared link.');
    }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Marketing Materials</div>
          <div className="page-subtitle">Branded assets, templates & digital resources for your practice</div>
        </div>
      </div>

      <div className="page-body">
        {/* SEARCH + FILTER */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              className="form-input"
              placeholder="Search assets..."
              style={{ paddingLeft: 32 }}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <span style={{ fontSize: 12, color: '#64748b' }}>{filtered.length} assets</span>
        </div>

        {/* TABS */}
        <div className="tabs">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              className={`tab ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* ASSETS GRID */}
        <div className="marketing-grid">
          {filtered.map(asset => {
            const Icon = asset.icon;
            return (
              <div key={asset.id} className="asset-card">
                <div className={`asset-preview ${asset.previewClass}`}>
                  <Icon size={40} color="rgba(16,73,124,0.3)" />
                  <div className="asset-type-badge">{asset.format}</div>
                </div>
                <div className="asset-info">
                  <div className="asset-name">{asset.name}</div>
                  <div className="asset-desc">{asset.desc}</div>
                  <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <span className="badge badge-primary">{asset.type}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8', alignSelf: 'center' }}>Updated {asset.updated}</span>
                  </div>
                  <div className="asset-actions">
                    <button className="btn btn-primary btn-sm" onClick={() => handleDownload(asset)}>
                      <Download size={12} /> Download
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleDownload(asset)}>
                      <ExternalLink size={12} /> Preview
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
            <Megaphone size={40} style={{ marginBottom: 12 }} />
            <div style={{ fontWeight: 500 }}>No assets found</div>
            <div style={{ fontSize: 13 }}>Try adjusting your search or category filter.</div>
          </div>
        )}

        {/* REQUEST NEW MATERIALS */}
        <div style={{
          marginTop: 32, background: 'white', border: '1px solid #e2e8f0',
          borderRadius: 10, padding: '20px 24px', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', gap: 16
        }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Need custom marketing materials?</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
              We can create co-branded assets specific to your practice.
            </div>
          </div>
          <a href="mailto:marketing@neurogenx.com?subject=Custom Marketing Request" className="btn btn-outline">
            Request Custom Assets
          </a>
        </div>
      </div>
    </>
  );
}
